// Landing page content, editable through the admin CMS (/admin/content).
//
// The frontend owns this schema and its defaults. The API only stores and
// returns a JSON blob under the key "landing_content"; whatever the admin
// editor saves is merged over these defaults, so a partial payload keeps the
// rest of the page intact.

export interface HeroLogLine {
  t: string;
  k: string;
  v: string;
}

export interface HeroSlide {
  eyebrow: string;
  title: string;
  emphasis?: string;
  description: string;
  image: string;
  primaryLabel: string;
  primaryTo: string;
  secondaryLabel: string;
  secondaryAction: string;
  insightTitle: string;
  insightBody: string;
  log: HeroLogLine[];
}

export interface StatItem {
  label: string;
  value: string;
  delta: string;
  kind: string;
}

export interface ServiceItem {
  title: string;
  text: string;
}

export interface StoryItem {
  tag: string;
  title: string;
  text: string;
  image: string;
}

export interface AudienceItem {
  title: string;
  text: string;
  points: string[];
  ctaLabel: string;
  ctaTo: string;
}

export interface StepItem {
  number: string;
  title: string;
  text: string;
}

export interface FaqItem {
  q: string;
  a: string;
}

export interface TestimonialItem {
  quote: string;
  name: string;
  role: string;
  image: string;
}

export interface FooterLink {
  label: string;
  href: string;
}

export interface FooterColumn {
  title: string;
  links: FooterLink[];
}

export interface SocialItem {
  platform: string;
  href: string;
}

export interface LandingContent {
  hero: {
    slides: HeroSlide[];
    avatars: string[];
    socialProofCount: string;
    socialProofLabel: string;
  };
  partnerHeading: string;
  partnerLogos: string[];
  stats: StatItem[];
  approach: {
    kicker: string;
    titleLine1: string;
    titleEmphasis: string;
    lead: string;
    ctaLabel: string;
  };
  services: {
    kicker: string;
    title: string;
    lead: string;
    items: ServiceItem[];
  };
  howItWorks: {
    kicker: string;
    title: string;
    titleEmphasis: string;
    lead: string;
    steps: StepItem[];
  };
  audiences: {
    kicker: string;
    title: string;
    lead: string;
    items: AudienceItem[];
  };
  results: {
    kicker: string;
    title: string;
    titleEmphasis: string;
    lead: string;
    stories: StoryItem[];
  };
  testimonials: {
    kicker: string;
    title: string;
    lead: string;
    featured: TestimonialItem;
    items: TestimonialItem[];
  };
  faq: {
    kicker: string;
    title: string;
    lead: string;
    items: FaqItem[];
  };
  cta: {
    title: string;
    titleEmphasis: string;
    text: string;
    primaryLabel: string;
    secondaryLabel: string;
  };
  footer: {
    tagline: string;
    columns: FooterColumn[];
    socials: SocialItem[];
    copyright: string;
    legal: string[];
  };
}

export const defaultLandingContent: LandingContent = {
  hero: {
    slides: [
      {
        eyebrow: "Work-Integrated Learning Network",
        title:
          "A Work-Integrated Learning Platform linking students to organizations for professional growth.",
        emphasis: "professional growth",
        description:
          "We empower students through structured professional experience, professional networking, and guided placement journeys.",
        image:
          "https://images.unsplash.com/photo-1523240795612-9a054b0db644?auto=format&fit=crop&w=1800&q=80",
        primaryLabel: "Explore solutions",
        primaryTo: "/auth/register",
        secondaryLabel: "How it works",
        secondaryAction: "how-it-works",
        insightTitle: "Institutional Confidence",
        insightBody:
          "Built for universities that need clarity across onboarding, placements, field supervision, and compliance reporting.",
        log: [
          { t: "08:45", k: "onboard", v: "142 students verified · Faculty of Commerce" },
          { t: "10:12", k: "approve", v: "Partner approved · NMB Bank · 12 slots" },
          { t: "13:30", k: "report", v: "Weekly compliance summary generated" },
        ],
      },
      {
        eyebrow: "Connecting Partners to Future Talent",
        title: "Help students grow with structured professional experience and mentorship.",
        emphasis: "mentorship",
        description:
          "Join our network to provide meaningful career opportunities and guide the next generation of professionals.",
        image:
          "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=1800&q=80",
        primaryLabel: "Partner with us",
        primaryTo: "/contact",
        secondaryLabel: "How it works",
        secondaryAction: "how-it-works",
        insightTitle: "Employer Operations",
        insightBody:
          "Reduce fragmented communication and give supervisors a more dependable workflow from assignment to final recommendation.",
        log: [
          { t: "09:02", k: "slots", v: "4 placements published · Software Engineering" },
          { t: "11:40", k: "review", v: "18 work logs approved by supervisors" },
          { t: "15:05", k: "recommend", v: "Recommendation issued · Amina J." },
        ],
      },
      {
        eyebrow: "Student Placement Journeys Made Visible",
        title: "Guide students from registration to certification in one connected experience.",
        emphasis: "certification",
        description:
          "Taskpart gives students a simpler path through readiness, placement, work logs, feedback, payments, and professional completion records.",
        image:
          "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&w=1800&q=80",
        primaryLabel: "Create account",
        primaryTo: "/auth/register",
        secondaryLabel: "Explore platform",
        secondaryAction: "platform",
        insightTitle: "Student Operations",
        insightBody:
          "Students experience a governed journey with clear readiness checks, placement matching, daily work logs, and verifiable certificates.",
        log: [
          { t: "09:02", k: "placement", v: "Matched · Software Engineering · Tigo" },
          { t: "11:40", k: "worklog", v: "8.0h submitted · awaiting supervisor" },
          { t: "16:30", k: "certify", v: "Certificate issued · verifiable link" },
        ],
      },
    ],
    avatars: [
      "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=200&q=80",
      "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=200&q=80",
      "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?auto=format&fit=crop&w=200&q=80",
    ],
    socialProofCount: "500+",
    socialProofLabel: "Institutions trust us globally",
  },
  partnerHeading: "Trusted by forward-thinking institutions and employers",
  partnerLogos: ["ACME CORP", "GLOBAL TECH", "AFRICA BANK", "NEXUS EDU", "SYNERGY"],
  stats: [
    { label: "On-Time Placements", value: "98.6%", delta: "+6.2% vs last month", kind: "chart-line" },
    { label: "Total Achievement", value: "$245,000", delta: "6.2% vs last month", kind: "chart-bar-dark" },
    { label: "Operational Saving", value: "$245,000", delta: "18.7% vs last month", kind: "chart-donut" },
    { label: "Network Health", value: "Excellent", delta: "All systems operational", kind: "status" },
  ],
  approach: {
    kicker: "Our approach",
    titleLine1: "Clarity. Control.",
    titleEmphasis: "Performance.",
    lead: "Gain complete visibility and control over your placement operations with real-time insight and smarter decisions from start to finish.",
    ctaLabel: "Learn more",
  },
  services: {
    kicker: "What we do",
    title: "Comprehensive services built around your programs",
    lead: "From strategy to execution, our tailored services help optimize every step of the placement journey.",
    items: [
      { title: "Placement Strategy", text: "Data-driven placement programs aligned with university goals and industry demand." },
      { title: "Capacity Planning", text: "Accurate forecasting and slot planning to balance student demand with real partner capacity." },
      { title: "Verification & Onboarding", text: "Documented academic checks, partner approvals, and role assignments without losing traceability." },
      { title: "Supervision Workflows", text: "End-to-end supervision that keeps assignments, reviews, and recommendations consistent." },
      { title: "Work Log Operations", text: "Reduce manual follow-up and improve completion tracking with structured daily logs." },
      { title: "Governance & Compliance", text: "Identify risk early and stay compliant with audit-ready operational workflows." },
    ],
  },
  howItWorks: {
    kicker: "How it works",
    title: "A clear journey from readiness to certification",
    titleEmphasis: "certification",
    lead: "Five governed steps that students, partners, and institutions follow together.",
    steps: [
      { number: "01", title: "Register & Verify", text: "Students and partners create accounts, upload documents, and complete structured onboarding." },
      { number: "02", title: "Approve & Prepare", text: "Institution teams validate readiness, review entities, and activate the right people." },
      { number: "03", title: "Publish Placements", text: "Partners define capacity and universities coordinate assignment through governed flows." },
      { number: "04", title: "Track in Real Time", text: "Students log daily activity while supervisors review and maintain accountability." },
      { number: "05", title: "Report & Certify", text: "The network turns activity into reports, certificates, and audit-ready records." },
    ],
  },
  audiences: {
    kicker: "Who it serves",
    title: "Three tailored experiences. One operational language.",
    lead: "Students, employers, and institutions each get a focused workspace while sharing the same trusted operational record.",
    items: [
      { title: "For Students", text: "A guided journey from registration through placement, work logs, reports, and certification.", points: ["Verified onboarding", "Live placement progress", "Reports & certificates"], ctaLabel: "Create student account", ctaTo: "/auth/register" },
      { title: "For Partners", text: "Publish placement slots, supervise interns, review work logs, and issue recommendations cleanly.", points: ["Slot publishing", "Centralized reviews", "Recommendation flows"], ctaLabel: "Partner with Taskpart", ctaTo: "/onboarding/partner" },
      { title: "For Universities", text: "One command layer for approvals, governance, placements, reports, and audit logs.", points: ["Approvals & oversight", "Audit-ready logs", "Operational reporting"], ctaLabel: "Explore the network", ctaTo: "/auth/login" },
    ],
  },
  results: {
    kicker: "Success stories",
    title: "Real results. Real impact.",
    titleEmphasis: "Real impact.",
    lead: "We partner with universities and industry leaders to solve complex placement challenges.",
    stories: [
      { tag: "University Programs", title: "Reduced Coordination Time by 64%", text: "A national university unified placement operations across 8 faculties and cut weekly coordination time dramatically.", image: "https://images.unsplash.com/photo-1523240795612-9a054b0db644?auto=format&fit=crop&w=1400&q=80" },
      { tag: "Industry Partners", title: "Improved Supervisor Visibility by 40%", text: "A finance partner streamlined intern supervision, work log review, and recommendation issuance from one dashboard.", image: "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=1400&q=80" },
    ],
  },
  testimonials: {
    kicker: "Our clients say",
    title: "Trusted by programs around the world",
    lead: "Our partners count on us to deliver solutions that create measurable and lasting impact.",
    featured: {
      quote: "Taskpart transformed our placement operations. They brought clarity, efficiency, and real value at every stage of the student journey.",
      name: "Joel Mwita",
      role: "Director of Placements, National University",
      image: "https://images.unsplash.com/photo-1531123897727-8f129e1688ce?auto=format&fit=crop&w=200&q=80",
    },
    items: [
      { quote: "Their data-driven approach helped us make better decisions and improve our intern outcomes.", name: "David Chen", role: "VP Operations, TechCore", image: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=200&q=80" },
      { quote: "A reliable partner who truly understands placement programs and delivers results.", name: "Sarah Williams", role: "Program Manager, BrightMart", image: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=200&q=80" },
    ],
  },
  faq: {
    kicker: "FAQ",
    title: "Questions decision-makers ask",
    lead: "Clear answers for institutions, employers, and teams evaluating a structured placement platform.",
    items: [
      { q: "What is Taskpart Network?", a: "Taskpart Network is a Work-Integrated Learning Platform that bridges academic learning and professional industry experience, linking students with partner organizations for structured growth." },
      { q: "How does it support students?", a: "Students get a complete professional experience workflow from skill verification and placement matching to work log mentorship and final certification." },
      { q: "Can it support placement and wider learning?", a: "Yes. The network includes professional courses, seminars, and certification paths designed to make students truly workforce-ready." },
      { q: "Why does Taskpart emphasize compliance and reporting?", a: "Serious placement programs depend on traceable approvals, work-log review, payment oversight, reporting, and certification. Governance is a core capability, not an afterthought." },
    ],
  },
  cta: {
    title: "Ready to build a smarter placement program?",
    titleEmphasis: "smarter",
    text: "Let's create a placement experience that is agile, efficient, and built for sustainable growth.",
    primaryLabel: "Talk to an expert",
    secondaryLabel: "Create account",
  },
  footer: {
    tagline: "Smart placement operations for a more connected and resilient education-to-industry pipeline.",
    columns: [
      { title: "Platform", links: [{ label: "Overview", href: "#platform" }, { label: "For Students", href: "#audiences" }, { label: "For Partners", href: "#audiences" }, { label: "For Universities", href: "#audiences" }] },
      { title: "Services", links: [{ label: "Placement Strategy", href: "#services" }, { label: "Capacity Planning", href: "#services" }, { label: "Supervision", href: "#services" }, { label: "Compliance", href: "#services" }] },
      { title: "Resources", links: [{ label: "Case Studies", href: "#results" }, { label: "Webinars", href: "#results" }, { label: "Guides", href: "#faq" }, { label: "FAQ", href: "#faq" }] },
      { title: "Company", links: [{ label: "About Us", href: "#platform" }, { label: "Careers", href: "/contact" }, { label: "Contact", href: "/contact" }] },
    ],
    socials: [
      { platform: "facebook", href: "#" },
      { platform: "instagram", href: "#" },
      { platform: "twitter", href: "#" },
      { platform: "linkedin", href: "#" },
    ],
    copyright: "© 2026 Taskpart Connect. Designed for professional placement operations.",
    legal: ["Privacy", "Terms", "Cookies"],
  },
};

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

// mergeLandingContent overlays saved content on the defaults. Objects merge
// recursively so a partial payload keeps untouched fields; arrays and scalars
// replace outright, so an admin can deliberately clear or shorten a list.
export function mergeLandingContent(base: unknown, override: unknown): LandingContent {
  if (!isPlainObject(base) || !isPlainObject(override)) {
    return base as LandingContent;
  }
  const out: Record<string, unknown> = { ...base };
  for (const [key, value] of Object.entries(override)) {
    const current = out[key];
    if (isPlainObject(value) && isPlainObject(current)) {
      out[key] = mergeLandingContent(current, value);
    } else if (value !== undefined && value !== null) {
      out[key] = value;
    }
  }
  return out as unknown as LandingContent;
}
